import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Divider,
  Paper,
  Autocomplete,
} from "@mui/material";
import api from "../services/api";
import { useSnackbarAlert } from "../hooks/useSnackbarAlert";

const QuickCostCalculator = () => {
  const snackbar = useSnackbarAlert();
  const [ingredients, setIngredients] = useState([]);
  const [entries, setEntries] = useState([]);
  const [batchSize, setBatchSize] = useState(1);
  const [laborMinutes, setLaborMinutes] = useState(0);
  const [laborRate, setLaborRate] = useState(20);
  const [wasteBufferPercent, setWasteBufferPercent] = useState(0);
  const [packagingCost, setPackagingCost] = useState(0);
  const [testPrice, setTestPrice] = useState(0);
  const [unitCost, setUnitCost] = useState(null);

  const targetMargin = 83.33;
  const targetPrice = unitCost ? unitCost / (1 - targetMargin / 100) : null;

  useEffect(() => {
    api
      .get("/admin/ingredients")
      .then((res) => setIngredients(res.data))
      .catch(() => snackbar.error("Failed to load ingredients."));
  }, []);

  const convertToBaseUnit = (unit) => {
    switch (unit) {
      case "kg":
        return 1000;
      case "g":
        return 1;
      case "l":
        return 1000;
      case "ml":
        return 1;
      case "pcs":
        return 1;
      default:
        return 1;
    }
  };

  const getCostPerRecipeUnit = (ingredient, recipeUnit) => {
    const totalQtyInBase =
      parseFloat(ingredient.unitAmount || 1) *
      convertToBaseUnit(ingredient.unit);
    const costPerBaseUnit =
      parseFloat(ingredient.costPerUnit || 0) / totalQtyInBase;
    return costPerBaseUnit * convertToBaseUnit(recipeUnit);
  };

  const handleEntryChange = (index, key, value) => {
    const updated = [...entries];
    updated[index][key] = value;
    setEntries(updated);
  };

  const addEntry = () => {
    setEntries([
      ...entries,
      { ingredientId: "", quantity: 0, recipeUnit: "g" },
    ]);
  };

  const removeEntry = (index) => {
    const updated = [...entries];
    updated.splice(index, 1);
    setEntries(updated);
  };

  const calculateCost = () => {
    let ingredientTotal = 0;

    for (const entry of entries) {
      const ingredient = ingredients.find((i) => i._id === entry.ingredientId);
      if (!ingredient) continue;

      const costPerRecipeUnit = getCostPerRecipeUnit(
        ingredient,
        entry.recipeUnit
      );
      const qty = parseFloat(entry.quantity || 0);
      ingredientTotal += costPerRecipeUnit * qty;
    }

    const laborCost = (parseFloat(laborMinutes) / 60) * parseFloat(laborRate);
    const wasteCost =
      (ingredientTotal + packagingCost) *
      (parseFloat(wasteBufferPercent) / 100);
    const totalCost = ingredientTotal + packagingCost + laborCost + wasteCost;
    const perUnit = totalCost / batchSize;

    setUnitCost(parseFloat(perUnit.toFixed(2)));
  };

  const marginAtPrice =
    testPrice && unitCost
      ? (((testPrice - unitCost) / testPrice) * 100).toFixed(1)
      : null;

  return (
    <Box p={{ xs: 2, sm: 3 }}>
      <Typography variant="h4" gutterBottom>
        Quick Cost Calculator
      </Typography>

      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Ingredients
        </Typography>

        {entries.map((entry, idx) => (
          <Grid
            container
            spacing={2}
            alignItems="center"
            key={idx}
            sx={{ mb: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={ingredients}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                value={
                  ingredients.find((i) => i._id === entry.ingredientId) || null
                }
                onChange={(_, selected) =>
                  handleEntryChange(
                    idx,
                    "ingredientId",
                    selected ? selected._id : ""
                  )
                }
                renderInput={(params) => (
                  <TextField {...params} label="Ingredient" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                type="number"
                label="Qty"
                fullWidth
                value={entry.quantity}
                onChange={(e) =>
                  handleEntryChange(idx, "quantity", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                label="Unit"
                fullWidth
                value={entry.recipeUnit}
                onChange={(e) =>
                  handleEntryChange(idx, "recipeUnit", e.target.value)
                }
                SelectProps={{ native: true }}>
                <option value="g">g</option>
                <option value="kg">kg</option>
                <option value="ml">ml</option>
                <option value="l">l</option>
                <option value="pcs">pcs</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Button
                variant="outlined"
                color="error"
                fullWidth
                onClick={() => removeEntry(idx)}>
                Remove
              </Button>
            </Grid>
          </Grid>
        ))}
        <Button onClick={addEntry} variant="contained" fullWidth sx={{ mt: 2 }}>
          + Add Ingredient
        </Button>
      </Paper>

      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Batch Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Batch Size"
              type="number"
              fullWidth
              value={batchSize}
              onChange={(e) => setBatchSize(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Packaging Cost"
              type="number"
              fullWidth
              value={packagingCost}
              onChange={(e) => setPackagingCost(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Waste Buffer %"
              type="number"
              fullWidth
              value={wasteBufferPercent}
              onChange={(e) => setWasteBufferPercent(e.target.value)}
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 3 }}>
          Optional Labor
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Labor Minutes"
              type="number"
              fullWidth
              value={laborMinutes}
              onChange={(e) => setLaborMinutes(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Labor Rate (per hour)"
              type="number"
              fullWidth
              value={laborRate}
              onChange={(e) => setLaborRate(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      <Button
        variant="contained"
        color="primary"
        onClick={calculateCost}
        fullWidth
        sx={{ py: 1.25 }}>
        Calculate
      </Button>

      {unitCost !== null && (
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Results
          </Typography>
          <Typography>Total Unit Cost: ${unitCost.toFixed(2)}</Typography>

          <TextField
            label="Test Selling Price ($)"
            type="number"
            fullWidth
            sx={{ mt: 2 }}
            value={testPrice}
            onChange={(e) => setTestPrice(parseFloat(e.target.value))}
          />

          {marginAtPrice && (
            <Typography
              sx={{ mt: 1 }}
              color={marginAtPrice < 83.33 ? "error" : "success.main"}>
              Gross Margin at ${testPrice.toFixed(2)}:{" "}
              <strong>{marginAtPrice}%</strong>
            </Typography>
          )}

          {targetPrice && (
            <Typography sx={{ mt: 2 }}>
              To achieve <strong>{targetMargin}%</strong> margin, sell at:{" "}
              <strong>${targetPrice.toFixed(2)}</strong>
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default QuickCostCalculator;
